import { AVATAR_COLOURS } from 'definitions';

export type AvatarColour = (typeof AVATAR_COLOURS)[number];

type NamedUser = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

/**
 * The OKLCH hue behind each badge colour. `avatarFill` fills the badge at one OKLCH
 * lightness and chroma, so every colour looks equally strong and white
 * initials reach at least 4.9:1 on all of them. OKLCH rather than HSL because its hues
 * are spaced as the eye sees them: the first palette was pastel HSL, where only the
 * ring showed the colour and neighbours looked alike.
 *
 * Red, orange and amber (below 110 and above 350) are left out, so a badge beside the
 * notification bell never reads as an alert. That leaves 240 degrees for ten colours,
 * about 25 apart.
 */
export const AVATAR_HUES: Record<AvatarColour, number> = {
  olive: 125,
  green: 148,
  emerald: 168,
  teal: 190,
  sky: 225,
  blue: 255,
  indigo: 275,
  violet: 295,
  purple: 318,
  pink: 345,
};

/**
 * The badge fill for a hue. Set as `--avatar-fill` and applied with `bg-(--avatar-fill)`,
 * never a custom CSS utility: `cn` only drops shadcn's `bg-muted` from the avatar
 * fallback when it recognises the caller's class as a background, and it does not know
 * what a custom utility sets. The first two versions used one, and `bg-muted` won.
 *
 * The same fill in light and dark mode: it is how a teammate is recognised, so it does
 * not change with the theme. Initials on it are always white.
 */
export const avatarFill = (hue: number) => `oklch(0.52 0.13 ${hue})`;

/** Coprime with ten: consecutive ids step three colours along, about 75 degrees apart. */
const AUTOMATIC_STEP = 3;

export const isAvatarColour = (value: unknown): value is AvatarColour =>
  typeof value === 'string' && (AVATAR_COLOURS as readonly string[]).includes(value);

/**
 * A profile photo is stored inline in `users.avatar` as a data URL, which every user join
 * then sends along with the row: the library repeats it per experiment someone created,
 * and an experiment page repeats it per activity-log entry (PER-124 is to stop that). So
 * the cap is well under the column's 512000 characters, and only a little above what the
 * dashboard sends: it crops and resizes to 256px JPEG before uploading, which lands
 * around 20 to 60 thousand characters.
 */
export const AVATAR_DATA_URL_MAX_LENGTH = 100_000;

const AVATAR_DATA_URL = /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

/** A data URL of a JPEG, PNG or WebP image, within the size cap. Anything else is refused. */
export const isAvatarDataUrl = (value: unknown): value is string =>
  typeof value === 'string' && value.length <= AVATAR_DATA_URL_MAX_LENGTH && AVATAR_DATA_URL.test(value);

/**
 * The colour someone picked, or else one keyed on their id rather than their name, so a
 * rename never changes it. A fixed step rather than a hash: a team is usually created
 * together, its ids are consecutive, and consecutive ids then land on clearly different
 * colours rather than neighbours, while ten in a row still use all ten.
 */
export function avatarColour({ id, avatarColour: chosen }: { id: number; avatarColour?: string | null }) {
  if (isAvatarColour(chosen)) return chosen;
  return AVATAR_COLOURS[(Math.abs(id) * AUTOMATIC_STEP) % AVATAR_COLOURS.length] ?? AVATAR_COLOURS[0];
}

export const avatarHue = (user: { id: number; avatarColour?: string | null }) => AVATAR_HUES[avatarColour(user)];

export function userFullName({ firstName, lastName, email }: NamedUser) {
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  if (name) return name;
  return email?.split('@')[0] ?? '';
}

type FirstNamed = { firstName?: string | null; lastName?: string | null } | null | undefined;

/**
 * The name a person goes by in a shared AI thread: their first name, else their last name, else
 * null (each chat says "Someone" or "A colleague"). The editor's and the results AI's.
 */
export const firstNameOf = (user: FirstNamed): string | null =>
  user?.firstName?.trim() || user?.lastName?.trim() || null;

/** Empty when there is nothing to take letters from; the caller shows an icon instead. */
export function userInitials({ firstName, lastName, email }: NamedUser) {
  const first = firstName?.trim()[0] ?? '';
  const last = lastName?.trim()[0] ?? '';
  const initials = `${first}${last}` || (email?.trim()[0] ?? '');
  return initials.toUpperCase();
}
